import { call, takeEvery, put, fork, take } from 'redux-saga/effects'
import { isEmpty, reduce, get, filter, includes, isNull } from 'lodash-es'
import { generateActions } from '../../utils/bundleFeature'
import { intToBool, LocalStorage } from '../../utils'
import {
  createPoll,
  updatePoll,
  setOptions,
  fetchPoll,
  deletePoll,
  vote,
} from './api'
import { getPollState, pollStateParamMap } from './utils'
import { showNotice } from '../../../Layout/Notices/Notices'
import { getLiveId } from '../../../global/blocs/roomInfo'

const answeredPollsStorageKey = 'webinar-answered-polls'

const getOptions = (options) => {
  const optionsArray = reduce(
    options,
    (acc, value, key) => {
      if (key !== 'question' && !isEmpty(value)) {
        acc.push(value)
      }

      return acc
    },
    []
  )

  return JSON.stringify(optionsArray)
}

function* createPollWorker(objectId, question) {
  const { poll } = yield call(createPoll, objectId, question)
  return poll.poll_id
}

function* updatePollWorker(pollId, question) {
  yield call(updatePoll, pollId, { question })
  return pollId
}

const getPreviouslyAnsweredPollIds = (isVotingAllowed) => {
  const answeredPolls = LocalStorage.get(answeredPollsStorageKey)
  if (isNull(answeredPolls) || !isVotingAllowed) {
    return []
  }

  return JSON.parse(answeredPolls)
}

export default ({ actionNames, props = {} }) => {
  const {
    objectToken,
    isPublicPollOnly,
    siblingFeatureNameSpace,
    isVotingAllowed,
    onInvalidatePolls,
  } = props

  const objectId = getLiveId()
  const previouslyAnsweredPollIds =
    getPreviouslyAnsweredPollIds(isVotingAllowed)

  const updateAnsweredPollIdsStorage = (pollId) => {
    const newAnsweredPolls = [...previouslyAnsweredPollIds, pollId]
    LocalStorage.set(answeredPollsStorageKey, JSON.stringify(newAnsweredPolls))
  }

  // filter polls that were answered before
  const filterPolls = (polls) => {
    if (isEmpty(previouslyAnsweredPollIds)) {
      return polls
    }

    return filter(polls, (poll) => {
      const wasAnswered = includes(previouslyAnsweredPollIds, poll.poll_id)
      if (!wasAnswered) {
        return true
      }

      // keep the call if it is not open (most probably published)
      return !intToBool(poll.open_p)
    })
  }

  function* fetchPollsWorker() {
    const data = isPublicPollOnly ? { public_p: 1 } : {}

    try {
      const { polls } = yield call(fetchPoll, objectId, objectToken, data)
      yield put({
        type: actionNames.FETCH_POLLS_SUCCESS,
        polls: filterPolls(polls),
      })
    } catch (err) {
      // Unhandled.
    }
  }

  function* updateSibling(actionType) {
    if (isEmpty(siblingFeatureNameSpace)) {
      return
    }

    const actionTypeUpper = actionType.toUpperCase()
    const { actionNames: siblingActionNames } = generateActions(
      siblingFeatureNameSpace,
      [actionTypeUpper]
    )
    yield put({ type: siblingActionNames[actionTypeUpper] })
  }

  function* saveEditWorker({ values, submitPromise, pollId }) {
    const { question } = values
    const { resolve, reject } = submitPromise
    const options = getOptions(values)
    const isNewPoll = isEmpty(pollId)
    const apiFunc = isNewPoll ? createPollWorker : updatePollWorker
    const id = isNewPoll ? objectId : pollId

    try {
      const updatePollId = yield call(apiFunc, id, question)
      yield call(setOptions, updatePollId, options)
      yield call(fetchPollsWorker)
      if (onInvalidatePolls) {
        yield call(onInvalidatePolls)
      }

      if (isNewPoll) {
        yield put({ type: actionNames.TOGGLE_CREATE_POLL_FORM_VISIBILITY })
      } else {
        yield put({ type: actionNames.TOGGLE_EDIT_POLL, pollId })
      }
      resolve()
    } catch (err) {
      showNotice({
        message: err.message,
        expirationMillis: 5e3,
        isClosable: true,
      })
      reject()
    }
  }

  function* deletePollWorker({ pollId }) {
    try {
      yield call(deletePoll, pollId)
      yield call(fetchPollsWorker)
      if (onInvalidatePolls) {
        yield call(onInvalidatePolls)
      }
      yield fork(updateSibling, 'refetch')
    } catch (err) {
      // Unhandled.
    }
  }

  function* activatePollWorker(poll) {
    const pollId = poll.poll_id
    const state = getPollState(poll)
    const params = get(pollStateParamMap, state, {})

    try {
      yield call(updatePoll, pollId, params)
      yield fork(fetchPollsWorker)
      yield fork(updateSibling, 'refetch')
    } catch (err) {
      showNotice({
        message: err.message,
        expirationMillis: 5e3,
        isClosable: true,
      })
    }
  }

  function* init() {
    yield fork(fetchPollsWorker)
  }

  function* watchActivatePoll() {
    while (true) {
      const { poll } = yield take(actionNames.ACTIVATE_POLL)
      yield call(activatePollWorker, poll)
    }
  }

  function* voteWorker({ poll, optionId, uuid }) {
    const pollId = poll.poll_id
    try {
      yield call(vote, objectId, objectToken, pollId, uuid, optionId)
      yield put({ type: actionNames.SET_ANSWERED_POLL_ID, pollId })

      updateAnsweredPollIdsStorage(pollId)
    } catch (err) {
      // Unhandled.
    }
  }

  function* watchAnswerPoll() {
    while (true) {
      const action = yield take(actionNames.ANSWER_POLL)
      yield call(voteWorker, action)
    }
  }

  function* actionWatchers() {
    yield takeEvery(actionNames.ON_CREATE_EDIT_SUBMIT, saveEditWorker)
    yield takeEvery(actionNames.DELETE_POLL, deletePollWorker)
    yield takeEvery(actionNames.REFETCH, fetchPollsWorker)
    yield fork(watchAnswerPoll)
    yield fork(watchActivatePoll)
  }

  return {
    default: [init(), actionWatchers()],
  }
}
